import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Divider,
  Text,
  Title2,
  Title3,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  DocumentPdf24Regular,
} from "@fluentui/react-icons";
import TimeGantt from "./components/TimeGantt";

function number(value) {
  return Number(value || 0);
}

function getHours(row = {}) {
  return number(
    row.hours ??
      row.quantity ??
      row.qty ??
      row.timeHours ??
      row.estimatedHours ??
      row.meta?.hours
  );
}

function getActivity(row = {}) {
  return (
    row.activity ||
    row.category ||
    row.phase ||
    row.workType ||
    row.meta?.activity ||
    row.meta?.category ||
    "General work"
  );
}

function getSection(row = {}) {
  const meta = row.meta || {};

  return (
    row.section ||
    row.sourceLabel ||
    meta.section ||
    meta.sourceLabel ||
    meta.sourceTypeLabel ||
    meta.sourceType ||
    "General"
  );
}

function getLabel(row = {}) {
  return row.label || row.name || row.description || "Untitled activity";
}

function normalizeRows(estimate = {}) {
  const timeRows =
    estimate.time ||
    estimate.timeRows ||
    estimate.activities ||
    estimate.timeActivities ||
    [];

  if (Array.isArray(timeRows) && timeRows.length > 0) {
  return timeRows.map((row, index) => ({
    id: row.id || `time_${index}`,
    section: getSection(row),
    activity: getActivity(row),
    label: getLabel(row),
    hours: getHours(row),
    source: row.source || "Time",
    assumptions:
      row.assumptions ||
      row.note ||
      row.notes ||
      row.meta?.assumptions ||
      "",
  }));
}

return (estimate.labour || []).map((row, index) => ({
  id: row.id || `labour_${index}`,
  section: getSection(row),
  activity: getActivity(row),
  label: getLabel(row),
  hours: getHours(row),
  source: "Labour",
  assumptions:
    row.assumptions ||
    row.note ||
    row.notes ||
    row.meta?.assumptions ||
    "",
    }));
}


function groupRows(rows) {
  return rows.reduce((acc, row) => {
    const section = row.section || "General";
    const activity = row.activity || "General work";
    const key = `${section}__${activity}`;

    if (!acc[key]) {
      acc[key] = {
        key,
        section,
        activity,
        rows: [],
        totalHours: 0,
      };
    }

    acc[key].rows.push(row);
    acc[key].totalHours += number(row.hours);

    return acc;
  }, {});
}

function downloadCsv(rows) {
  const headers = ["Activity", "Description", "Hours", "Source", "Assumptions"];

  const body = rows.map((row) =>
    [
      row.activity,
      row.label,
      row.hours,
      row.source,
      row.assumptions,
    ]
      .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
      .join(",")
  );

  const csv = [headers.join(","), ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "time-estimate.csv";
  link.click();

  URL.revokeObjectURL(url);
}

function exportPdf(groups, totalHours, parsedData = {}) {
  const projectTitle =
    parsedData?.projectName ||
    parsedData?.customerName ||
    parsedData?.address ||
    "Quote time estimate";

  const html = `
    <html>
      <head>
        <title>Time Estimate</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            color: #201f1e;
            padding: 32px;
          }

          h1 {
            margin: 0;
            font-size: 24px;
          }

          .subtitle {
            margin-top: 6px;
            color: #605e5c;
            font-size: 13px;
          }

          .summary {
            margin-top: 24px;
            padding: 14px 16px;
            background: #f3f2f1;
            border: 1px solid #edebe9;
            font-size: 16px;
            font-weight: bold;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 24px;
            font-size: 13px;
          }

          th {
            text-align: left;
            background: #f3f2f1;
            color: #605e5c;
            border-bottom: 1px solid #edebe9;
            padding: 8px;
          }

          td {
            border-bottom: 1px solid #edebe9;
            padding: 8px;
            vertical-align: top;
          }

          .group {
            background: #faf9f8;
            font-weight: bold;
          }

          .right {
            text-align: right;
          }
        </style>
      </head>

      <body>
        <h1>Time Estimate</h1>
        <div class="subtitle">${projectTitle}</div>

        <div class="summary">
          Total estimated time: ${totalHours.toFixed(2)} hours
        </div>

        <table>
          <thead>
            <tr>
              <th>Activity</th>
              <th>Description</th>
              <th class="right">Hours</th>
              <th>Assumptions</th>
            </tr>
          </thead>
          <tbody>
            ${Object.values(groups)
              .map(
                (group) => `
                  <tr class="group">
                    <td colspan="2">${group.activity}</td>
                    <td class="right">${group.totalHours.toFixed(2)}</td>
                    <td></td>
                  </tr>

                  ${group.rows
                    .map(
                      (row) => `
                        <tr>
                          <td></td>
                          <td>${row.label}</td>
                          <td class="right">${number(row.hours).toFixed(2)}</td>
                          <td>${row.assumptions || ""}</td>
                        </tr>
                      `
                    )
                    .join("")}
                `
              )
              .join("")}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

export default function TimeWorkspace({
  estimate,
  parsedData,
  onUpdateTimeRows,
}) {
  const [expanded, setExpanded] = useState({});

  const baseRows = useMemo(() => normalizeRows(estimate), [estimate]);
  const [rows, setRows] = useState(baseRows);

  useEffect(() => {
    setRows(baseRows);
  }, [baseRows]);

  const groups = useMemo(() => groupRows(rows), [rows]);
  const groupList = Object.values(groups);

  const totalHours = useMemo(
    () => rows.reduce((sum, row) => sum + number(row.hours), 0),
    [rows]
  );

  const largestGroup = groupList.reduce(
    (largest, group) =>
      group.totalHours > largest.totalHours ? group : largest,
    { activity: "—", totalHours: 0 }
  );

  function toggle(activity) {
  setExpanded((prev) => ({
    ...prev,
    [activity]: prev[activity] === false,
  }));
}

function updateRow(index, field, value) {
  if (index < 0) return;

  const next = [...rows];

  next[index] = {
    ...next[index],
    [field]: field === "hours" ? Number(value) : value,
  };

  setRows(next);
  onUpdateTimeRows?.(next);
}

function addRow() {
  const next = [
    ...rows,
    {
      id: `manual_${Date.now()}`,
      activity: "New activity",
      label: "",
      hours: 0,
      source: "Manual",
      assumptions: "",
    },
  ];

  setRows(next);
  onUpdateTimeRows?.(next);
}

function removeRow(index) {
  if (index < 0) return;

  const next = rows.filter((_, i) => i !== index);
  setRows(next);
  onUpdateTimeRows?.(next);
}

  return (
    <div className="h-full overflow-auto bg-[#f3f2f1] p-6 text-[#201f1e]">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Title2>Time Estimate</Title2>
          <div className="mt-1">
            <Text size={300} className="text-[#605e5c]">
              Summarized labour time, activity groups, and quote assumptions.
            </Text>
          </div>
        </div>

        <div className="flex gap-2">
  <Button appearance="primary" onClick={addRow}>
    + Add time row
  </Button>

  <Button
    appearance="secondary"
    icon={<ArrowDownload24Regular />}
    onClick={() => downloadCsv(rows)}
  >
    Export CSV
  </Button>

  <Button
    appearance="primary"
    icon={<DocumentPdf24Regular />}
    onClick={() => exportPdf(groups, totalHours, parsedData)}
  >
    Export PDF
  </Button>
</div>
</div>

      <div className="mb-5 grid gap-4 md:grid-cols-3">
        <Card>
          <Text size={200} weight="semibold" className="text-[#605e5c]">
            TOTAL TIME
          </Text>
          <div className="mt-2">
            <Text size={700} weight="bold">
              {totalHours.toFixed(2)}
            </Text>
            <Text className="ml-2 text-[#605e5c]">hours</Text>
          </div>
        </Card>

        <Card>
          <Text size={200} weight="semibold" className="text-[#605e5c]">
            ACTIVITY GROUPS
          </Text>
          <div className="mt-2">
            <Text size={700} weight="bold">
              {groupList.length}
            </Text>
          </div>
        </Card>

        <Card>
          <Text size={200} weight="semibold" className="text-[#605e5c]">
            LARGEST ACTIVITY
          </Text>
          <div className="mt-2">
            <Text weight="bold">{largestGroup.activity}</Text>
          </div>
          <Text size={300} className="text-[#605e5c]">
            {largestGroup.totalHours.toFixed(2)} hours
          </Text>
        </Card>
      </div>

      <Card>
        <CardHeader
          header={<Title3>Activity Breakdown</Title3>}
          description="Grouped time assumptions generated from labour or explicit time rows"
        />

        <Divider />

        {groupList.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Text className="text-[#605e5c]">
              No time rows found. Add labour rows or future estimate.time rows.
            </Text>
          </div>
        ) : (
          <div className="pt-3">
            <div className="overflow-hidden rounded-sm border border-[#edebe9]">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="h-8 border-b border-[#edebe9] bg-[#f3f2f1] text-left text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                    <th className="w-[42%] px-3">Activity / description</th>
                    <th className="w-[120px] px-3 text-right">Hours</th>
                    <th className="w-[120px] px-3">Source</th>
                    <th className="px-3">Assumptions</th>
                  </tr>
                </thead>

                <tbody>
                  {groupList.map((group) => {
                    const isOpen = expanded[group.key] !== false;

                    return (
                      <React.Fragment key={group.activity}>
                        <tr className="h-10 border-b border-[#edebe9] bg-[#faf9f8]">
                          <td className="px-3 font-semibold text-[#201f1e]">
                            <button
                              type="button"
                              onClick={() => toggle(group.key)}
                              className="mr-2 text-[#605e5c]"
                            >
                              {isOpen ? "▾" : "▸"}
                            </button>
                            <div className="inline-flex flex-col">
                                <span>{group.section}</span>
                                <span className="text-xs font-normal text-[#605e5c]">
                               {group.activity}
                             </span>
                            </div>
                            <Badge appearance="tint" color="brand" className="ml-2">
                              {group.rows.length} rows
                            </Badge>
                          </td>

                          <td className="px-3 text-right font-semibold">
                            {group.totalHours.toFixed(2)}
                          </td>

                          <td className="px-3 text-[#605e5c]">Grouped</td>
                          <td className="px-3 text-[#605e5c]">
                            Activity total
                          </td>
                        </tr>

                        {isOpen &&
  group.rows.map((row) => {
    const index = rows.findIndex((item) => item.id === row.id);

    return (
      <tr
        key={row.id}
        className="h-9 border-b border-[#edebe9] bg-white hover:bg-[#f3f2f1]"
      >
        <td className="px-3 text-[#323130]">
          <input
            value={row.label || ""}
            onChange={(e) => updateRow(index, "label", e.target.value)}
            className="ml-6 w-[calc(100%-1.5rem)] rounded-sm border border-transparent bg-transparent px-1 text-[13px] focus:border-[#0f6cbd] focus:bg-white focus:outline-none"
            placeholder="Description"
          />
        </td>

        <td className="px-3 text-right font-medium text-[#323130]">
          <input
            type="number"
            value={row.hours ?? 0}
            onChange={(e) => updateRow(index, "hours", e.target.value)}
            className="w-full rounded-sm border border-transparent bg-transparent px-1 text-right text-[13px] focus:border-[#0f6cbd] focus:bg-white focus:outline-none"
          />
        </td>

        <td className="px-3 text-[#605e5c]">
          <input
            value={row.activity || ""}
            onChange={(e) => updateRow(index, "activity", e.target.value)}
            className="w-full rounded-sm border border-transparent bg-transparent px-1 text-[13px] focus:border-[#0f6cbd] focus:bg-white focus:outline-none"
            placeholder="Activity"
          />
        </td>

        <td className="px-3 text-[#605e5c]">
          <div className="flex items-center gap-2">
            <input
              value={row.assumptions || ""}
              onChange={(e) =>
                updateRow(index, "assumptions", e.target.value)
              }
              className="w-full rounded-sm border border-transparent bg-transparent px-1 text-[13px] focus:border-[#0f6cbd] focus:bg-white focus:outline-none"
              placeholder="Assumptions..."
            />

            <button
              type="button"
              onClick={() => removeRow(index)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-[#605e5c] hover:bg-[#fde7e9] hover:text-[#a4262c]"
              title="Remove time row"
            >
              ×
            </button>
          </div>
        </td>
      </tr>
    );
  })}
  </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      <TimeGantt estimate={estimate} />

    </div>
  );
}